import { PublicClientApplication } from '@azure/msal-browser';
import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

// project imports
import Loader from 'components/Loader';
import { APP_DEFAULT_PATH } from 'config';
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';
import axios from 'utils/axios';

// api
import { updateLastLogin, validateUserAccess } from 'api/auth';

// ==============================|| INITIAL STATE ||============================== //

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// ==============================|| SESSION ||============================== //

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| MSAL CONFIG ||============================== //

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID}`,
    redirectUri: import.meta.env.VITE_ENTRA_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: '/login'
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  }
};

const loginRequest = {
  scopes: ['openid', 'profile', 'email', import.meta.env.VITE_ENTRA_API_SCOPE]
};

// Microsoft Graph scopes
const graphScopes = import.meta.env.VITE_ENTRA_GRAPH_SCOPES?.split(/[,\s]+/).filter(Boolean) || ['User.Read'];

// ==============================|| CONTEXT ||============================== //

const EntraContext = createContext(null);

// ==============================|| PROVIDER ||============================== //

export const EntraProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const msalInitialized = useRef(false);

  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), []);

  // ==============================|| MSAL HELPERS ||============================== //

  const setActiveAccountIfNeeded = useCallback(() => {
    const current = msalInstance.getActiveAccount();
    if (!current) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0]);
    }
    return msalInstance.getActiveAccount();
  }, [msalInstance]);

  const acquireApiToken = useCallback(
    async (forceRefresh = false) => {
      const account = setActiveAccountIfNeeded();
      if (!account) return null;

      try {
        const result = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh
        });
        return result.accessToken;
      } catch (error) {
        console.error('Error acquiring API token:', error);

        try {
          const result = await msalInstance.acquireTokenPopup(loginRequest);
          return result.accessToken;
        } catch (popupError) {
          console.error('Error acquiring token via popup:', popupError);
          return null;
        }
      }
    },
    [msalInstance, setActiveAccountIfNeeded]
  );

  // Token for Microsoft Graph
  const acquireGraphToken = useCallback(async () => {
    const account = setActiveAccountIfNeeded();
    if (!account) return null;

    try {
      const result = await msalInstance.acquireTokenSilent({ scopes: graphScopes, account });
      return result.accessToken;
    } catch (error) {
      console.error('Error acquiring Graph token:', error);
      return null;
    }
  }, [msalInstance, setActiveAccountIfNeeded]);

  // Fetch profile from Graph
  const fetchGraphProfile = useCallback(async () => {
    try {
      const graphToken = await acquireGraphToken();

      if (!graphToken) return null;

      const res = await fetch('https://graph.microsoft.com/v1.0/me?$select=displayName,jobTitle,department,mail', {
        headers: { Authorization: `Bearer ${graphToken}` }
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('Error fetching Graph profile:', e);
      return null;
    }
  }, [acquireGraphToken]);

  // ==============================|| BACKEND VALIDATION ||============================== //

  /**
   * Validate user access in the backend and update last-login
   * @param {string} apiToken - Access token from Entra ID
   * @param {object} account - MSAL account
   * @param {object} graphMe - Microsoft Graph profile
   * @returns {Promise<boolean>} - true if the user has access, false otherwise
   */
  const validateAndInitializeUser = useCallback(async (apiToken, account, graphMe) => {
    try {
      //console.log('🔐 Validating user access with backend...');

      // 1. Validate user access in backend
      const validationResult = await validateUserAccess(apiToken);

      if (!validationResult.success || !validationResult.data) {
        // User does not have access (403 or 401)
        //console.warn('User without access:', validationResult.error);
        setSession(null);

        // Clear user data from localStorage
        localStorage.removeItem('userRoles');
        localStorage.removeItem('userPermissions');
        //localStorage.removeItem('userWarehouseId');

        dispatch({ type: LOGOUT });

        const currentPath = window.location.pathname;

        if (!currentPath.startsWith('/maintenance')) {
          // Set flag to avoid redirect loops
          sessionStorage.setItem('auth-validation-failed', 'true');
          window.location.href = '/maintenance/403';
        }

        return false;
      }

      const userData = validationResult.data;
      //console.log('✅ User validated successfully');

      // Clear any previous validation failure flag
      sessionStorage.removeItem('auth-validation-failed');

      // 2. Persist user data in localStorage
      localStorage.setItem('userRoles', JSON.stringify(userData.roles || []));
      localStorage.setItem('userPermissions', JSON.stringify(userData.permissions || []));
      //localStorage.setItem('userWarehouseId', userData.warehouseId?.toString() || '');

      // 3. Update state with combined information from Entra + Graph + Backend
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: {
            // IDs from Entra
            id: account.localAccountId,
            //sub: userData.sub, // ID from backend
            email: account.username,
            name: account.name,
            // Data from Microsoft Graph (jobTitle, department, etc.)
            jobTitle: graphMe?.jobTitle ?? undefined,
            department: graphMe?.department ?? undefined,
            mail: graphMe?.mail ?? undefined,
            displayName: graphMe?.displayName ?? account.name,
            // Data from backend
            permissions: userData.permissions,
            roles: userData.roles
            //warehouseId: userData.warehouseId
          }
        }
      });

      // 4. Update last-login (non-blocking)
      updateLastLogin(apiToken).catch((error) => {
        console.warn('Unable to update last-login:', error);
      });

      return true;
    } catch (error) {
      console.error('Unexpected error during user validation:', error);
      setSession(null);
      dispatch({ type: LOGOUT });
      return false;
    }
  }, []);

  // ==============================|| INITIALIZE ||============================== //

  useEffect(() => {
    //let mounted = true;

    const init = async () => {
      try {
        const currentPath = window.location.pathname;
        const isMaintenancePage = currentPath.startsWith('/maintenance');
        const isLoginPage = currentPath.startsWith('/login') || currentPath === '/';
        //console.log('App initialized at path:', currentPath);
        const validationFailed = sessionStorage.getItem('auth-validation-failed') === 'true';

        if (isMaintenancePage && validationFailed) {
          //console.log('Skipping initialization - user already redirected to maintenance page');
          msalInitialized.current = false;

          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            accounts.forEach((account) => {
              try {
                msalInstance.setActiveAccount(null);
              } catch (e) {
                console.warn('Error clearing account:', e);
              }
            });
          }

          dispatch({ type: LOGOUT });
          return;
        }

        //console.log('🔧 Initializing MSAL...');
        await msalInstance.initialize();
        msalInitialized.current = true;
        //console.log('✅ MSAL initialized successfully');

        // Handle response from redirect (if any)
        let redirectResult = null;

        try {
          redirectResult = await msalInstance.handleRedirectPromise();
          if (redirectResult?.account) {
            //console.log('✅ Redirect result detected, setting active account:', redirectResult.account.username);
            msalInstance.setActiveAccount(redirectResult.account);
          }
        } catch (redirectError) {
          console.warn('Error handling redirect promise:', redirectError);
        }

        const account = setActiveAccountIfNeeded();

        if (account) {
          //console.log('👤 Active account found');
          const forceRefresh = !!redirectResult;

          // 1. Get token for API backend
          const apiToken = await acquireApiToken(forceRefresh);

          if (!apiToken) {
            //console.error('❌ Failed to acquire API token');
            dispatch({ type: LOGOUT });
            return;
          }

          //console.log('✅ API token acquired');

          // 2. Get profile from Microsoft Graph (jobTitle, department, etc.)
          const graphMe = await fetchGraphProfile();
          //console.log('✅ Microsoft Graph profile fetched:', graphMe);

          // 3. Set session
          setSession(apiToken);

          // 4. Validate access in backend
          const isValid = await validateAndInitializeUser(apiToken, account, graphMe);
          //console.log('isLoginPage result:', isLoginPage);

          if (isValid && isLoginPage) {
            //console.log('✅ User validated, redirecting to dashboard...');
            window.location.href = APP_DEFAULT_PATH;
          }
        } else {
          //console.log('ℹ️ No active account found');
          // No active account
          dispatch({ type: LOGOUT });
        }
      } catch (error) {
        console.error('Error during MSAL initialization:', error);
        msalInitialized.current = true;

        dispatch({ type: LOGOUT });
      }
    };

    init().finally(() => {
      /*if (mounted) {
        // Mark as initialized
        dispatch((prev) => ({
          ...prev,
          type: prev?.type ?? undefined,
          payload: { ...prev?.payload, isInitialized: true }
        }));
      }*/
    });

    return () => {
      //mounted = false;
    };
  }, [acquireApiToken, fetchGraphProfile, msalInstance, setActiveAccountIfNeeded, validateAndInitializeUser]);

  // ==============================|| LOGIN ||============================== //

  const login = useCallback(async () => {
    try {
      //console.log('Login initiated...');
      //console.log('MSAL initialized flag:', msalInitialized.current);

      if (!msalInitialized.current) {
        //console.log('Initializing MSAL for login...');
        await msalInstance.initialize();
        msalInitialized.current = true;
      }

      // Clear any previous validation failure flag
      sessionStorage.removeItem('auth-validation-failed');

      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      //console.error('Login error:', error);
      msalInitialized.current = false;
      throw error;
    }
  }, [msalInstance]);

  // ==============================|| LOGOUT ||============================== //

  const logout = useCallback(async () => {
    try {
      //console.log('🚪 Logout initiated...');
      setSession(null);

      // Clear localStorage
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
      //localStorage.removeItem('userWarehouseId');

      // Clear any previous validation failure flag
      sessionStorage.removeItem('auth-validation-failed');

      msalInitialized.current = false;

      if (!msalInitialized.current) {
        await msalInstance.initialize();
        msalInitialized.current = true;
      }

      await msalInstance.logoutRedirect();
      dispatch({ type: LOGOUT, payload: { isLoggedIn: false, user: null } });
    } catch (error) {
      console.error('Logout error:', error);
      msalInitialized.current = false;
      dispatch({ type: LOGOUT, payload: { isLoggedIn: false, user: null } });
    }
  }, [msalInstance]);

  // ==============================|| GET ACCESS TOKEN ||============================== //

  const getAccessToken = useCallback(async () => {
    const token = await acquireApiToken();
    if (token) setSession(token);
    return token;
  }, [acquireApiToken]);

  // ==============================|| RENDER ||============================== //

  // Show loader until initialized
  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <EntraContext.Provider
      value={{
        ...state,
        login,
        logout,
        getAccessToken
      }}
    >
      {children}
    </EntraContext.Provider>
  );
};

export default EntraContext;
